/**
 * src/features/auth/lib/lastLoginMethod.ts
 *
 * AUTH-09 / roadmap criterion 3 — remembers WHICH method the user last signed in with
 * (never WHO — see the no-email guarantee below), so LoginCard can offer it first on the
 * next visit.
 *
 * Two facts worth knowing before touching this file:
 *   1. The key name below is pinned by roadmap criterion 3 and, once released, will live in
 *      real users' browsers. Renaming it later does not clean up the old key in browsers
 *      that already signed in once — that would need a migration or a deliberate decision
 *      to abandon the stale key, not a casual rename.
 *   2. It is the first storage key in this project to use a colon (see LAST_LOGIN_METHOD_KEY
 *      below) instead of a hyphen — every other key here (`estimatix-project`, `estimatix-ui`,
 *      ...) uses a hyphen. This is a new naming convention, introduced deliberately by this
 *      file, not an inconsistency to "fix" — grep for existing keys accordingly.
 *
 * This file stays a pure function over the browser's storage API: no React, no i18next, no
 * better-auth client import. That is what makes it testable without jsdom's component stack
 * and, just as importantly, what makes a grep for the write call a meaningful safety check
 * for the no-email guarantee (AUTH-09 prohibition) — there is exactly one such call site in
 * the whole auth slice, and it lives here.
 */

/** The three — and only three — sign-in methods this screen offers. Not `string`: modeling
 * this as a free-form string (CLAUDE.md rule 7) would let anything end up driving the
 * Primary/Secondary block order. */
export type LoginMethod = 'google' | 'password' | 'magic-link';

export const LAST_LOGIN_METHOD_KEY = 'estimatix:last_login_method';

/**
 * Second, transient key — WR-02. GoogleSignInButton's click is a full-page navigation with
 * no client-observed success moment (see GoogleSignInButton.tsx's header comment), so its
 * write to LAST_LOGIN_METHOD_KEY on click is optimistic. This key carries the method that
 * was remembered *before* that optimistic write, across the navigation, so a LATE failure —
 * the user returns to LoginCard via a failed OAuth callback, a code path that never re-runs
 * GoogleSignInButton's own synchronous rollback — can still be told apart from a real success
 * and rolled back to the actual prior preference, not just cleared to "nothing remembered".
 * The sentinel string `'none'` represents "nothing was remembered before this click"
 * (localStorage cannot store a literal `null`). Like LAST_LOGIN_METHOD_KEY, this key never
 * holds an email or any credential — only ever one of the same three method literals, or the
 * sentinel.
 */
const PENDING_GOOGLE_ROLLBACK_KEY = 'estimatix:last_login_method_pending_rollback';

function isLoginMethod(value: string | null): value is LoginMethod {
  return value === 'google' || value === 'password' || value === 'magic-link';
}

/**
 * Reads the remembered method. Never throws, never returns anything outside the union:
 * an unfamiliar value (garbage typed into DevTools, a future app version's method, or the
 * key simply not existing yet) all collapse to `null`, which callers treat as "nothing
 * remembered" → default Primary/Secondary order (42-UI-SPEC.md §1).
 *
 * The try/catch here guards a genuinely throwing browser API — private browsing / disabled
 * site data can make `localStorage` access throw — so the sign-in screen, the single door
 * into the product, must never fail on it. This is NOT the same shape as the try/catch this
 * project forbids around better-auth calls (which never throw, WR-01) — this one wraps a
 * real throwing API.
 */
export function getLastLoginMethod(): LoginMethod | null {
  try {
    const raw = localStorage.getItem(LAST_LOGIN_METHOD_KEY);
    return isLoginMethod(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Writes the remembered method, or clears it when passed `null` ("nothing remembered").
 * One function instead of a separate `clearLastLoginMethod` — GoogleSignInButton's
 * optimistic-write rollback needs to restore "nothing remembered" as a value, and adding a
 * third export just for that would widen this file's surface without need.
 *
 * Failures are swallowed silently, without diagnostic output: a failed write only means the
 * next visit won't remember the method, which is a UX-memory nice-to-have, not something the
 * user needs to be told about.
 */
export function setLastLoginMethod(method: LoginMethod | null): void {
  try {
    if (method === null) {
      localStorage.removeItem(LAST_LOGIN_METHOD_KEY);
    } else {
      localStorage.setItem(LAST_LOGIN_METHOD_KEY, method);
    }
  } catch {
    // Swallowed intentionally — see doc comment above.
  }
}

/**
 * Captures the method that was remembered right before GoogleSignInButton's optimistic
 * 'google' write (WR-02). Call once, synchronously, immediately before that write. Failures
 * are swallowed the same way as setLastLoginMethod — a failed capture only means a late
 * failure won't be able to roll back, degrading to the pre-existing "optimistic write
 * sticks" behavior, not a crash.
 */
export function captureGoogleRollbackCandidate(previousMethod: LoginMethod | null): void {
  try {
    localStorage.setItem(PENDING_GOOGLE_ROLLBACK_KEY, previousMethod ?? 'none');
  } catch {
    // Swallowed intentionally — see doc comment above.
  }
}

/**
 * Reads and clears the rollback candidate captured by captureGoogleRollbackCandidate.
 *
 * Returns `undefined` when no candidate is present (no Google click happened since the last
 * consume, or storage failed) — callers must treat that as "do not touch
 * LAST_LOGIN_METHOD_KEY", which is distinct from a captured `null` ("nothing was remembered
 * before that click"), itself a valid, meaningful restore target. A corrupted stored value
 * (neither `'none'` nor a known LoginMethod) degrades safely to `null`, mirroring
 * getLastLoginMethod's own unknown-value handling — this never throws and never restores
 * garbage into LAST_LOGIN_METHOD_KEY.
 */
export function consumeGoogleRollbackCandidate(): LoginMethod | null | undefined {
  let raw: string | null;
  try {
    raw = localStorage.getItem(PENDING_GOOGLE_ROLLBACK_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) {
    return undefined;
  }
  try {
    localStorage.removeItem(PENDING_GOOGLE_ROLLBACK_KEY);
  } catch {
    // Swallowed — the stale key may linger, but the value already read above is still
    // honored below; a future consume attempt would just see the same not-yet-cleared
    // candidate again.
  }
  if (raw === 'none') {
    return null;
  }
  return isLoginMethod(raw) ? raw : null;
}
